import axios from 'axios';
import { fetchCalendarMonth } from './calendarService';
import { logError, logWarning, logMissingConfig } from '../utils/errorHandlers';

const MINI_CATEGORY_TO_TYPE = {
    MINI_OD: 'OD',
    MINI_OE: 'OE',
};

const DEV_STUB_TOURNAMENTS = {
    od: [
        {
            tournamentId: '2026-03-12_OD',
            type: 'OD',
            date: '2026-03-12',
            title: 'Offenes Doppel – Placeholder #1',
            startDateTime: '2026-03-12T20:00:00+01:00',
        },
        {
            tournamentId: '2026-03-19_OD',
            type: 'OD',
            date: '2026-03-19',
            title: 'Offenes Doppel – Placeholder #2',
            startDateTime: '2026-03-19T20:00:00+01:00',
        },
    ],
    oe: [
        {
            tournamentId: '2026-03-14_OE',
            type: 'OE',
            date: '2026-03-14',
            title: 'Offenes Einzel – Placeholder',
            startDateTime: '2026-03-14T20:00:00+01:00',
        },
    ],
};

const BERLIN_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

const BERLIN_DATE_PARTS_FORMATTER = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
});

const toDate = (value) => {
    if (value instanceof Date) {
        return new Date(value.getTime());
    }

    if (typeof value === 'number') {
        return new Date(value);
    }

    if (typeof value === 'string') {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed;
        }
    }

    return new Date();
};

const formatBerlinDateLabel = (value) => {
    const date = toDate(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return BERLIN_DATE_FORMATTER.format(date);
};

const getBerlinYearMonth = (value) => {
    const date = toDate(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    const parts = BERLIN_DATE_PARTS_FORMATTER.formatToParts(date).reduce((acc, part) => {
        if (part.type === 'year' || part.type === 'month' || part.type === 'day') {
            acc[part.type] = Number.parseInt(part.value, 10);
        }
        return acc;
    }, {});

    if (!parts.year || !parts.month) {
        return null;
    }

    return {
        year: parts.year,
        month: parts.month,
        day: parts.day ?? 1,
    };
};

const incrementMonth = ({ year, month }) => {
    let nextYear = year;
    let nextMonth = month + 1;
    if (nextMonth > 12) {
        nextMonth = 1;
        nextYear += 1;
    }
    return { year: nextYear, month: nextMonth };
};

const getMonthsToCover = (referenceDate) => {
    const current = getBerlinYearMonth(referenceDate);
    if (!current) {
        return [];
    }

    const currentMonth = { year: current.year, month: current.month };
    return [currentMonth, incrementMonth(currentMonth)];
};

const getCalendarApiBase = () => process.env.REACT_APP_TOURNAMENT_CALENDAR_API_URL?.trim() ?? '';

const buildUpcomingEndpoint = () => {
    const raw = getCalendarApiBase();
    if (!raw) {
        return '';
    }

    const normalized = raw.replace(/\s+/g, '').length === 0 ? '' : raw.trim();
    if (!normalized) {
        return '';
    }

    const withoutTrailingSlash = normalized.replace(/\/+$/, '');
    if (/\/api\/tournaments\/next$/i.test(withoutTrailingSlash)) {
        return withoutTrailingSlash;
    }

    return `${withoutTrailingSlash}/api/tournaments/next`;
};

function cloneStubTournaments() {
    return {
        od: DEV_STUB_TOURNAMENTS.od.map((entry) => ({ ...entry })),
        oe: DEV_STUB_TOURNAMENTS.oe.map((entry) => ({ ...entry })),
    };
}

function sanitizeUpcomingPayload(payload) {
    return {
        od: Array.isArray(payload?.od) ? payload.od : [],
        oe: Array.isArray(payload?.oe) ? payload.oe : [],
    };
}

const isTournamentSummaryResponse = (payload) =>
    Boolean(payload && Array.isArray(payload.od) && Array.isArray(payload.oe));

const mapSnapshotEventsToUpcoming = (events = [], referenceDate) => {
    const todayLabel = formatBerlinDateLabel(referenceDate ?? new Date());
    if (!todayLabel) {
        return { od: [], oe: [] };
    }

    const normalized = events
        .map((event, index) => {
            if (!event || typeof event !== 'object') {
                return null;
            }

            const type = MINI_CATEGORY_TO_TYPE[event.flyerCategory];
            if (!type) {
                return null;
            }

            const date = typeof event.date === 'string' ? event.date : null;
            if (!date || date < todayLabel) {
                return null;
            }

            const payload = {
                tournamentId: event.tournamentId || event.flyerId || (date ? `${date}_${type}` : null),
                type,
                date,
                title: event.title || '',
                startDateTime: event.startDateTime || event.start || null,
            };

            return {
                index,
                date,
                type,
                payload,
            };
        })
        .filter(Boolean)
        .sort((a, b) => {
            const dateCompare = a.date.localeCompare(b.date);
            if (dateCompare !== 0) {
                return dateCompare;
            }

            const startA = a.payload.startDateTime || '';
            const startB = b.payload.startDateTime || '';
            if (startA !== startB) {
                return startA.localeCompare(startB);
            }

            const idCompare = (a.payload.tournamentId || '').localeCompare(b.payload.tournamentId || '');
            if (idCompare !== 0) {
                return idCompare;
            }

            return a.index - b.index;
        });

    const result = { od: [], oe: [] };
    for (const entry of normalized) {
        if (entry.type === 'OD' && result.od.length < 2) {
            result.od.push(entry.payload);
        } else if (entry.type === 'OE' && result.oe.length < 1) {
            result.oe.push(entry.payload);
        }

        if (result.od.length === 2 && result.oe.length === 1) {
            break;
        }
    }

    return result;
};

const fetchSnapshotEvents = async (referenceDate) => {
    const monthsToLoad = getMonthsToCover(referenceDate);
    if (monthsToLoad.length === 0) {
        return [];
    }

    const aggregated = [];
    for (const descriptor of monthsToLoad) {
        try {
            const payload = await fetchCalendarMonth(descriptor);
            if (Array.isArray(payload?.events) && payload.events.length > 0) {
                aggregated.push(...payload.events);
            }
        } catch (error) {
            logError('[tournamentCalendarService] Failed to fetch calendar snapshot month', error, { descriptor });
        }
    }

    return aggregated;
};

const loadUpcomingFromSnapshot = async (options = {}) => {
    try {
        const events = await fetchSnapshotEvents(options.referenceDate);
        if (!events.length) {
            return null;
        }
        return mapSnapshotEventsToUpcoming(events, options.referenceDate);
    } catch (error) {
        logError('[tournamentCalendarService] Calendar snapshot fallback failed', error);
        return null;
    }
};

async function loadUpcomingTournaments(options = {}) {
    const endpoint = buildUpcomingEndpoint();

    if (endpoint) {
        try {
            const response = await axios.get(endpoint);
            if (isTournamentSummaryResponse(response?.data)) {
                return sanitizeUpcomingPayload(response.data);
            }
            logWarning('[tournamentCalendarService]', 'Unexpected payload from /api/tournaments/next – falling back to calendar snapshot');
        } catch (error) {
            logError('[tournamentCalendarService] Failed to fetch /api/tournaments/next', error);
        }
    } else {
        logMissingConfig('tournamentCalendarService', 'REACT_APP_TOURNAMENT_CALENDAR_API_URL', '– falling back to calendar snapshot');
    }

    const snapshotResult = await loadUpcomingFromSnapshot(options);
    if (snapshotResult) {
        return snapshotResult;
    }

    logWarning('[tournamentCalendarService]', 'Falling back to stub tournaments');
    return cloneStubTournaments();
}

export async function fetchUpcomingTournaments(options = {}) {
    return loadUpcomingTournaments(options);
}
